import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useQuery} from "@tanstack/react-query";
import {AgGridReact} from "ag-grid-react";
import {FaEraser, FaFileAlt, FaPlusSquare, FaPrint} from "react-icons/fa";
import {useNavigate} from "react-router-dom";
import TemplateDefault from "../../components/template-default";
import Card from "../../components/card";
import Button from "../../components/button";
import Modal from "../../components/modal";
import InputForm from "../../components/input-form";
import SelectComponent from "../../components/select-component";
import InputCheckbox from "../../components/input-checkbox";
import CandidateSheet from "./components/candidate-sheet";
import {LocalizationAgGrid} from "../../utils/localization-ag-grid";
import {buildAccentInsensitiveColDef, setAccentInsensitiveQuickFilter} from "../../utils/ag-grid-quick-filter";
import {cvCandidateService} from "../../services/curriculum-resource-service";
import {ProjectContext} from "../../context/project-context";

const initialFilters = {
    department: null,
    position: null,
    gender: null,
    cnh: false,
    lives_in_itatiba: false,
};

const genderOptions = [
    {value: "Masculino", label: "Masculino"},
    {value: "Feminino", label: "Feminino"},
];

const normalizeCandidateSheet = (candidate) => ({
    ...candidate,
    department_id: candidate.department ? {
        value: candidate.department.id,
        label: candidate.department.name,
    } : candidate.department_id,
    position_id: candidate.position ? {
        value: candidate.position.id,
        label: candidate.position.name,
    } : candidate.position_id,
    benefits: candidate.benefits ?? [],
    qualifications: candidate.qualifications ?? [],
});

const CvCandidate = () => {
    const navigate = useNavigate();
    const {user} = useContext(ProjectContext);
    const isViewOnly = !user.permissions.includes("cv_candidates")
        && user.permissions.includes("cv_candidates_view_only");
    const gridRef = useRef();
    const [filters, setFilters] = useState(initialFilters);
    const [search, setSearch] = useState("");
    const [candidateSheetId, setCandidateSheetId] = useState(null);
    const openCandidateSheet = useCallback((candidateId) => {
        setCandidateSheetId(candidateId);
    }, []);
    const defaultColDef = useMemo(() => buildAccentInsensitiveColDef({resizable: false}), []);
    const columnDefs = useMemo(() => [
        {field: "department_name", headerName: "Departamento", width: 190},
        {field: "position_name", headerName: "Cargo", width: 150},
        {field: "name", headerName: "Nome", flex: 1, minWidth: 220},
        {
            field: "cnh",
            headerComponent: () => (
                <div className={"flex justify-center w-full"}>
                    <p>CNH</p>
                </div>
            ),
            cellRenderer: "agCheckboxCellRenderer",
            cellRendererParams: {disabled: true},
            cellClass: "flex items-center justify-center",
            width: 90,
        },
        {field: "city", headerName: "Cidade", flex: 1, minWidth: 160},
        {field: "gender", headerName: "Gênero", width: 130},
        {field: "age", headerName: "Idade", width: 90},
        {
            colId: "candidateSheet",
            headerName: "",
            width: 60,
            sortable: false,
            filter: false,
            cellRenderer: ({data}) => (
                <span onClick={() => openCandidateSheet(data.id)} title={"Ficha do candidato"}
                      className="w-[25px] h-[25px] m-auto flex justify-center items-center text-white bg-blue-700 cursor-pointer rounded-md">
                    <FaFileAlt size={"12px"}/>
                </span>
            ),
        },
    ], [openCandidateSheet]);

    const requestFilters = useMemo(() => ({
        department: filters.department?.value ?? null,
        position: filters.position?.value ?? null,
        gender: filters.gender?.value ?? null,
        cnh: filters.cnh,
        lives_in_itatiba: filters.lives_in_itatiba,
    }), [filters]);

    const {data: candidates = [], isFetching: isFetchingCandidates} = useQuery({
        queryFn: () => cvCandidateService.list(requestFilters).then(({data}) => data),
        queryKey: ["cv-candidates", requestFilters],
    });

    const {data: options = {}, isFetching: isFetchingOptions} = useQuery({
        queryFn: () => cvCandidateService.options().then(({data}) => data),
        queryKey: ["cv-candidates-options"],
    });

    useEffect(() => {
        if (!candidateSheetId) return undefined;

        document.body.classList.add("candidate-sheet-modal-print");

        return () => document.body.classList.remove("candidate-sheet-modal-print");
    }, [candidateSheetId]);

    const {
        data: candidateSheet,
        isFetching: isFetchingCandidateSheet,
        isError: isCandidateSheetError,
    } = useQuery({
        queryFn: () => cvCandidateService.getById(candidateSheetId)
            .then(({data}) => normalizeCandidateSheet(data)),
        queryKey: ["cv-candidate-sheet", candidateSheetId],
        enabled: !!candidateSheetId,
        retry: 0,
    });

    const handleSelect = (field, value) => {
        setFilters(prev => ({...prev, [field]: value}));
    };

    const handleCheckbox = ({target: {id, checked}}) => {
        setFilters(prev => ({...prev, [id]: checked}));
    };

    const handleSearch = useCallback(({target: {value}}) => {
        setSearch(value);
        setAccentInsensitiveQuickFilter(gridRef, value);
    }, []);

    const clearFilters = () => {
        setFilters(initialFilters);
        setSearch("");
        setAccentInsensitiveQuickFilter(gridRef, "");
    };

    return (
        <TemplateDefault title="Candidatos" isLoading={isFetchingCandidates || isFetchingOptions}>
            <Card>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-4">
                    {!isViewOnly && (
                        <Button col="col-span-2" onClick={() => navigate("/cv/candidates/form")}>
                            <FaPlusSquare/> Novo candidato
                        </Button>
                    )}
                    <SelectComponent instanceId="department" name="department" label="Departamento"
                                     options={options.departments ?? []} value={filters.department}
                                     onChange={(value) => handleSelect("department", value)}
                                     col={isViewOnly ? "col-span-5" : "col-span-3"}/>
                    <SelectComponent instanceId="position" name="position" label="Cargo"
                                     options={options.positions ?? []} value={filters.position}
                                     onChange={(value) => handleSelect("position", value)} col="col-span-3"/>

                    <div className={"flex gap-4 col-span-4"}>
                        <SelectComponent instanceId="gender" name="gender" label="Gênero"
                                         options={genderOptions} value={filters.gender}
                                         onChange={(value) => handleSelect("gender", value)} col="w-full"/>
                        <InputCheckbox id="cnh" label="Possui CNH" col={"w-[215px]"} checked={filters.cnh}
                                       onChange={handleCheckbox}/>
                        <InputCheckbox id="lives_in_itatiba" label="Reside em Itatiba" col={"w-[279px]"}
                                       checked={filters.lives_in_itatiba} onChange={handleCheckbox}/>
                    </div>

                    <div className="col-span-12 flex gap-4">
                        <InputForm id="search" name="search" label="Pesquisar" type="search"
                                   value={search} onChange={handleSearch} col="w-full"/>
                        <Button type="button" onClick={clearFilters} title="Limpar filtros">
                            <FaEraser/>
                        </Button>
                    </div>
                </div>

                <div className="ag-theme-quartz grid mt-4" style={{height: "calc(100vh - 210px)"}}>
                    <AgGridReact
                        ref={gridRef}
                        rowHeight={32}
                        rowData={candidates}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        pagination
                        paginationPageSize={500}
                        paginationPageSizeSelector={[10, 50, 100, 500]}
                        animateRows={false}
                        suppressRowClickSelection={true}
                        suppressCellFocus={true}
                        gridOptions={{localeText: LocalizationAgGrid}}
                        onCellClicked={({data, column}) => {
                            if (column.getColId() !== "candidateSheet") {
                                navigate(`/cv/candidates/form/${data.id}`);
                            }
                        }}
                    />
                </div>
            </Card>

            <Modal isOpen={!!candidateSheetId} onClose={() => setCandidateSheetId(null)}
                   title="Ficha do candidato" className="w-11/12 max-w-6xl" alignTop>
                <div className="max-h-[calc(100vh-100px)] overflow-y-auto p-4">
                    <div className="mb-3 flex justify-end print:hidden">
                        <Button type="button" target="primary" onClick={() => window.print()}
                                disabled={!candidateSheet || isFetchingCandidateSheet}>
                            <FaPrint/> Imprimir
                        </Button>
                    </div>

                    {isFetchingCandidateSheet && !candidateSheet && (
                        <p className="py-10 text-center text-gray-500">Carregando ficha...</p>
                    )}

                    {isCandidateSheetError && (
                        <p className="py-10 text-center text-red-600">Não foi possível carregar a ficha.</p>
                    )}

                    {candidateSheet && (
                        <CandidateSheet candidate={candidateSheet} candidateId={candidateSheetId} printable={false}/>
                    )}
                </div>
            </Modal>

            {candidateSheet && createPortal(
                <div className="candidate-sheet-print-portal">
                    <CandidateSheet candidate={candidateSheet} candidateId={candidateSheetId}/>
                </div>,
                document.body,
            )}
        </TemplateDefault>
    );
};

export default CvCandidate;
